import React, { RefObject, useEffect, useMemo, useState } from "react";
import { Card } from "./GanttCard";
import { n, OT_INK } from "./ganttTypes";
import type { GanttInputs, FullResult } from "./ganttTypes";
import { CHART, fmtH, fmtHm } from "./chartUtils";
import { fmtMassT } from "./ganttUnits";
import { useSettings } from "@/hooks/useSettings";
import { bufferStats, bufferReadout, BUF_SWATCH } from "./bufferChart";
import type { BufferReadout } from "./bufferChart";

interface BufferCardProps {
  inputs: GanttInputs;
  result: FullResult | null;
  bufCanvasRef: RefObject<HTMLCanvasElement>;
}

// Tooltip sits TIP_GAP px from the cursor; it flips left of it when an
// (estimated) TIP_W px wide box would not fit on the right.
const TIP_W = 150;
const TIP_GAP = 14;

/** Product buffer — cumulative incoming vs consumed vs frozen (canvas drawn by drawBufferChart). */
export function BufferCard({ inputs, result, bufCanvasRef }: BufferCardProps) {
  const { uiUnits: sys } = useSettings();
  const fmtT = (kg: number) => fmtMassT(kg, sys);   // "35.6 t" / "39.2 ton"
  const r = result?.main;
  const otMin = Math.max(0, r?.overtime_used_h ?? 0) * 60;
  const goalT = n(inputs.goalT);
  const hasGoal = goalT > 0;

  // The same figures the chart is drawn from (capped incoming, supply end, peak).
  const stats = useMemo(() => (r ? bufferStats(r, goalT) : null), [r, goalT]);
  const s = stats && stats.n > 0 ? stats : null;
  const peakT = s ? fmtT(s.peakKg) : "";
  const peakH = s ? fmtH(s.peakMin / 60) : "";
  const supplyH = s && s.supplyEndMin != null ? fmtH(s.supplyEndMin / 60) : "";
  const summary = s
    ? `Peak buffer ${peakT} at ${peakH}${supplyH ? ` · Supply ends ${supplyH}` : ""}`
    : "";

  // Hover read-out: DOM overlays on the canvas, which is never redrawn on hover.
  const [hover, setHover] = useState<{ ro: BufferReadout; my: number } | null>(null);
  // A redraw (new result or goal) moves everything under the cursor: drop the stale read-out.
  useEffect(() => { setHover(null); }, [r, goalT, sys]);

  const onMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    const my = e.clientY - box.top;
    const ro = bufferReadout(e.currentTarget, e.clientX - box.left, my);
    setHover(ro ? { ro, my } : null);
  };

  let overlay: React.ReactNode = null;
  if (hover) {
    const { ro, my } = hover;
    const roomR = ro.width - ro.x - TIP_GAP;
    const flip = roomR < TIP_W && ro.x - TIP_GAP > roomR;
    const tipStyle: React.CSSProperties = {
      ...(flip ? { right: ro.width - ro.x + TIP_GAP } : { left: ro.x + TIP_GAP }),
      // below the cursor in the top half of the plot, above it in the bottom half
      ...(my < (ro.top + ro.bottom) / 2 ? { top: my + TIP_GAP } : { bottom: ro.height - my + TIP_GAP }),
    };
    const rows: { label: string; color: string; kg: number }[] = [
      { label: "Incoming", color: CHART.incoming, kg: ro.inKg },
      { label: "Consumed", color: CHART.consumed, kg: ro.outKg },
      ...(ro.frozenKg != null ? [{ label: "Frozen", color: CHART.frozen, kg: ro.frozenKg }] : []),
      { label: "In buffer", color: BUF_SWATCH, kg: ro.bufKg },
    ];
    overlay = (
      <>
        <div className="chart-xhair" style={{ left: ro.x, top: ro.top, height: ro.bottom - ro.top }} />
        <div className="chart-dot" style={{ left: ro.x, top: ro.yIn, background: CHART.incoming }} />
        <div className="chart-dot" style={{ left: ro.x, top: ro.yOut, background: CHART.consumed }} />
        {ro.yFrozen != null && (
          <div className="chart-dot" style={{ left: ro.x, top: ro.yFrozen, background: CHART.frozen }} />
        )}
        <div className="chart-tip" style={tipStyle}>
          <div className="tip-hd">{fmtHm(ro.tMin)}</div>
          {rows.map(row => (
            <div key={row.label} style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
              <span><i style={{ background: row.color }} />{row.label}</span>
              <span>{fmtT(row.kg)}</span>
            </div>
          ))}
        </div>
      </>
    );
  }

  return (
    <Card
      title="Product buffer — incoming vs consumed"
      summary={summary}
    >
      <div className="buf-wrap chart-wrap">
        <canvas
          ref={bufCanvasRef}
          data-testid="gantt-buffer-chart"
          style={{ width: "100%", display: "block", height: 240 }}
          onMouseMove={onMove}
          onMouseLeave={() => setHover(null)}
        />
        {overlay}
      </div>
      <div className="legend" style={{ marginTop: 8 }}>
        <div><i style={{ background: CHART.incoming }} />Cumulative incoming</div>
        <div><i style={{ background: CHART.consumed }} />Cumulative consumed (loads)</div>
        <div><i style={{ background: CHART.frozen }} />Cumulative frozen</div>
        {hasGoal && <div><i style={{ background: CHART.goal }} />Goal</div>}
        {otMin > 0 && (
          <div><i style={{ background: "rgba(75,75,157,.16)", border: `1px dashed ${OT_INK}` }} />Overtime (unload only)</div>
        )}
      </div>
      {s && (
        <div className="chart-stats" data-testid="gantt-buffer-stats">
          <span>Peak buffer <b>{peakT}</b> at <b>{peakH}</b></span>
          {supplyH ? <span>Supply ends <b>{supplyH}</b></span> : null}
        </div>
      )}
      <p className="note">
        The consumed staircase can never rise above the incoming line — that is the inflow gate.
        Where the gap closes to zero before supply ends, a load is held until the next batch has arrived.
        Supply ends once the last batch is covered: no load uses product that arrives later, so the
        incoming line runs flat from there.
      </p>
    </Card>
  );
}
